<template>
  <v-card flat max-width="1280" class="pl-md-6">
    <guide-tour config-key="message.log"/>
    <div class="px-4 mt-4">
      <div>
        <div class="bullet">
          篩選發送訊息
        </div>
        <v-sheet color="grey-8 pa-5 mt-5">
          <v-row>
            <v-col cols="12" md="6">
              <v-menu
                ref="menu"
                v-model="dateMenu"
                :close-on-content-click="false"
                :return-value.sync="selectDate"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateRangeText"
                    readonly
                    clearable
                    hide-details
                    dense
                    outlined
                    label="請選擇開始日期與結束日期"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="selectDate"
                  no-title
                  scrollable
                  range
                  locale="zh"
                  :allowed-dates="allowedDates"
                >
                  <v-spacer/>
                  <v-btn text color="primary" @click="dateMenu = false">
                    取消
                  </v-btn>
                  <v-btn text color="primary" @click="$refs.menu.save(selectDate)">
                    確認
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="selectLogMode"
                :items="logModeOptions"
                return-object
                outlined
                dense
                label="請選擇要瀏覽的訊息"
                hide-details
              />
            </v-col>
            <v-col v-if="selectLogMode && canSearchPhone.includes(selectLogMode.value)" cols="12" md="6">
              <v-text-field
                v-model="searchPhone"
                outlined
                dense
                label="電話號碼(選填)"
                clearable
                hide-details
              />
            </v-col>
          </v-row>
          <div class="text-right mt-3">
            <v-btn
              color="primary"
              :disabled="loading || !(dateRangeText.length && selectLogMode)"
              @click="handleSearch"
            >
              確認篩選
            </v-btn>
          </div>
        </v-sheet>
      </div>
      <div v-if="logTitle" class="mt-8 pb-12">
        <div class="bullet d-flex align-center">
          {{ logTitle }}
          <div class="ml-auto">
            <v-btn color="primary" outlined :loading="downloadingLog" :disabled="downloadingLog" @click="downloadLog">
              <v-icon>mdi-file-download</v-icon>
              下載記錄
            </v-btn>
          </div>
        </div>
        <div class="mt-2 d-flex flex-column" style="position: relative;">
          <v-overlay :value="loading" absolute>
            <v-progress-circular
              indeterminate
              size="64"
            ></v-progress-circular>
          </v-overlay>
          <v-sheet
            v-for="(log, idx) in logData"
            :key="idx"
            :class="currentSelectName === 'lineGroup' ? 'message-log-card pa-5 mb-4' : 'pa-5 d-flex'"
            :color="currentSelectName === 'lineGroup' ? 'white' : (idx % 2 === 0 ? '#F5F5F5' : '')"
            :outlined="currentSelectName === 'lineGroup'"
            :rounded="currentSelectName === 'lineGroup' ? 'lg' : false"
          >
            <template v-if="currentSelectName === 'lineGroup'">
              <div class="line-message-header d-flex align-start justify-space-between">
                <div class="d-flex align-start min-width-0">
                  <v-chip
                    :color="lineGroupStatusMap(log.status).color"
                    class="flex-shrink-0 mr-3"
                    small
                    dark
                  >
                    {{ lineGroupStatusMap(log.status).text }}
                  </v-chip>
                  <div class="min-width-0">
                    <div class="line-message-recipient text-truncate">
                      <span class="line-message-recipient-label">傳送對象</span>
                      <span :class="{'line-message-recipient-value--empty': !hasLogValue(log.patient_name)}">
                        {{ lineGroupRecipientDisplay(log) }}
                      </span>
                    </div>
                    <div class="line-message-meta mt-1">
                      <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                      {{ log.create_time || '-' }}
                    </div>
                  </div>
                </div>
                <v-btn
                  class="line-message-download ml-4 flex-shrink-0"
                  color="grey darken-2"
                  outlined
                  small
                  :loading="downloadingDetailNo === log.no"
                  :disabled="isDetailDownloadDisabled(log)"
                  @click="downloadDetailLog(currentSelectName, log)"
                >
                  <v-icon small class="mr-1">mdi-file-download-outline</v-icon>
                  下載詳細名單
                </v-btn>
              </div>

              <v-divider class="my-4"/>

              <div v-if="!lineGroupHasAnyContent(log)" class="line-message-all-empty">
                <v-icon small color="grey lighten-1">mdi-message-off-outline</v-icon>
                <span class="ml-2">此筆紀錄沒有可顯示的訊息內容</span>
              </div>

              <div v-else class="line-message-body">
                <div class="min-width-0">
                  <div v-if="shouldUseStructuredLineGroupContent(log)" class="line-message-template-row">
                    <span class="line-message-field-label mb-0">使用套版</span>
                    <span
                      class="line-message-template-name"
                      :class="{'line-message-template-name--empty': !hasLogValue(log.template_name)}"
                    >
                      {{ lineGroupTemplateDisplay(log) }}
                    </span>
                  </div>
                  <div class="line-message-field-label">文字內容</div>
                  <div
                    class="line-message-text text-pre-line"
                    v-text="lineGroupTextDisplay(log)"
                  />
                </div>
                <div>
                  <div class="line-message-field-label">圖片內容</div>
                  <button
                    v-if="hasLogValue(log.image_url)"
                    type="button"
                    class="line-message-image-preview"
                    aria-label="放大查看群發圖片"
                    @click="openImagePreview(log.image_url)"
                  >
                    <v-img
                      :src="log.image_url"
                      width="128"
                      height="96"
                      contain
                    />
                  </button>
                  <div v-else class="line-message-empty-image">
                    <v-icon small color="grey lighten-1">mdi-image-off-outline</v-icon>
                    <span class="ml-2">無圖片</span>
                  </div>
                </div>
              </div>

              <div class="line-message-results">
                <div class="line-message-result-item">
                  <span class="line-message-result-label">文字結果</span>
                  <v-chip :color="lineGroupResultMap(log.text_result).color" x-small dark>
                    {{ lineGroupResultMap(log.text_result).text }}
                  </v-chip>
                </div>
                <div class="line-message-result-item">
                  <span class="line-message-result-label">圖片結果</span>
                  <v-chip :color="lineGroupResultMap(log.image_result).color" x-small dark>
                    {{ lineGroupResultMap(log.image_result).text }}
                  </v-chip>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="pr-4 flex-grow-1">
                <div v-if="currentSelectName === 'smsGroup'" class="mb-2">
                  <v-chip :color="statusMap(log.status).color" dark>{{ statusMap(log.status).text }}</v-chip>
                </div>
                <div v-if="['lineRemind', 'smsRemind', 'voiceRemind', 'smartCare', 'miniApp'].includes(currentSelectName)">
                  傳送對象：{{ log.patient_phone }} {{ log.patient_name }}
                </div>
                <div v-else>傳送對象：{{ log.patient_name }}</div>
                <div v-if="currentSelectName !== 'miniApp'" class="d-flex" :class="{'flex-wrap': $vuetify.breakpoint.smAndDown}">
                  <div style="min-width: fit-content;">
                    發送內容：
                  </div>
                  <div
                    class="text-pre-line"
                    :class="{'pl-4': $vuetify.breakpoint.smAndDown}"
                    style="width: 100%;"
                    v-text="log.content"
                  />
                </div>
                <div>發送時間：{{ log.create_time }}</div>
                <template v-if="currentSelectName === 'miniApp'">
                  <div v-if="log.message_type_text">訊息類型：{{ log.message_type_text }}</div>
                  <div>預約時間：{{ log.appointment_time || '-' }}</div>
                  <div>
                    查看詳情：
                    <a v-if="log.detail_url" :href="log.detail_url" target="_blank" rel="noopener noreferrer">連結</a>
                    <span v-else>-</span>
                  </div>
                </template>
                <div v-if="['smsRemind', 'voiceRemind'].includes(currentSelectName)">發送點數：使用點數 {{ log.point }} 點
                </div>
                <template v-if="currentSelectName === 'smsRemind'">
                  <div v-if="log.message_type_text">訊息類型：{{ log.message_type_text }}</div>
                  <div v-if="log.template_source_text">模板來源：{{ log.template_source_text }}</div>
                  <div v-if="log.payer_type_text">付款方：{{ log.payer_type_text }}</div>
                  <div v-if="log.sms_segments">簡訊封數：{{ log.sms_segments }} 封</div>
                </template>
                <div v-if="['smsRemind', 'voiceRemind'].includes(currentSelectName)">發送狀態：{{ log.status }}</div>
              </div>
              <div v-if="currentSelectName === 'smsGroup'" class="flex-shrink-1">
                <v-btn color="grey-3" depressed :loading="downloadingDetailNo === log.no" :disabled="isDetailDownloadDisabled(log)" @click="downloadDetailLog(currentSelectName, log)">
                  <v-icon>mdi-file-download</v-icon>
                  下載詳細名單
                </v-btn>
              </div>
            </template>
          </v-sheet>

          <v-pagination
            v-model="page"
            :length="length"
            :total-visible="7"
            class="mt-2"
          ></v-pagination>
        </div>
      </div>
    </div>
    <v-dialog v-model="imagePreviewDialog" max-width="900">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          圖片內容
          <v-btn icon aria-label="關閉圖片預覽" @click="closeImagePreview">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-img v-if="previewImageUrl" :src="previewImageUrl" max-height="70vh" contain/>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  downloadLineMessageGroupDetailLogsAPI,
  downloadLineMessageGroupLogsAPI,
  downloadLineMessageRemindLogsAPI,
  downloadSmsMessageGroupDetailLogsAPI, downloadSmsMessageGroupLogsAPI,
  downloadSmsRemindLogsAPI,
  downloadVoiceRemindLogsAPI,
  downloadSmartCareLogsAPI,
  downloadMiniAppMessageLogsAPI,
  getLineMessageGroupLogsAPI,
  getLineMessageRemindLogsAPI,
  getSMSMessageGroupLogAPI,
  getSmsRemindLogsAPI,
  getVoiceRemindLogsAPI,
  getSmartCareLogsAPI,
  getMiniAppMessageLogsAPI
} from '@/api';
import GuideTour from "@/components/utils/GuideTour.vue";
import { taiwanToday } from "@/utils/timezoneHelper";

export default {
  name: "MessageLog",

  components: {GuideTour},

  data() {
    return {
      loading: false,
      dateMenu: false,
      selectDate: [],
      selectLogMode: null,
      currentSelectName: null,
      confirmedLogFilter: null,
      logModeOptions: [
        {text: 'LINE 訊息發送記錄', value: 'lineRemind'},
        {text: '簡訊發送記錄', value: 'smsRemind'},
        {text: 'LINE 群發記錄', value: 'lineGroup'},
        {text: '簡訊群發記錄', value: 'smsGroup'},
        {text: '語音發送記錄', value: 'voiceRemind'},
        {text: 'LINE智能個管師訊息紀錄', value: 'smartCare'},
        {text: 'MINI App 訊息發送紀錄', value: 'miniApp'},
      ],
      canSearchPhone: ['lineRemind', 'smsRemind', 'voiceRemind', 'smartCare', 'miniApp'],
      searchPhone: null,
      logData: [],
      logTitle: null,
      page: 1,
      length: 1,
      searchLogTimer: null,
      searchRequestId: 0,
      isDestroyed: false,
      downloadingLog: false,
      downloadingDetailNo: null,
      imagePreviewDialog: false,
      previewImageUrl: null,
    }
  },

  computed: {
    ...mapGetters([
      'axiosParamData',
    ]),
    dateRangeText: {
      get: function () {
        return [...this.selectDate].sort().join(' ~ ')
      },
      set: function () {
        this.selectDate = []
      }
    },
    logAxiosData() {
      const data = {}
      const dates = this.confirmedLogFilter?.dates || []
      if (dates.length === 1) {
        data.start = data.end = dates[0]
      } else if (dates.length === 2) {
        data.start = dates[0]
        data.end = dates[1]
      }
      return data
    }
  },

  methods: {
    initializeFromRouteQuery() {
      const {mode, start, end} = this.$route.query
      if (mode !== 'lineGroup' || typeof start !== 'string' || typeof end !== 'string') return

      const startDate = this.$moment(start, 'YYYY-MM-DD', true)
      const endDate = this.$moment(end, 'YYYY-MM-DD', true)
      if (!startDate.isValid() || !endDate.isValid()) return

      const today = this.$moment(taiwanToday(), 'YYYY-MM-DD')
      const earliestDate = today.clone().subtract(90, 'days')
      const isOutsideAllowedDates = !startDate.isAfter(earliestDate, 'day') || startDate.isAfter(today, 'day') ||
        !endDate.isAfter(earliestDate, 'day') || endDate.isAfter(today, 'day')
      const isInvalidRange = endDate.isBefore(startDate, 'day') || endDate.diff(startDate, 'days') >= 30
      if (isOutsideAllowedDates || isInvalidRange) return

      const logMode = this.logModeOptions.find(option => option.value === mode)
      if (!logMode) return

      this.selectLogMode = logMode
      this.selectDate = start === end ? [start] : [start, end]
      this.handleSearch()
    },
    statusMap(statusCode) {
      switch (Number(statusCode)) {
        case 1:
          return {
            color: '#F9D401',
            text: '等待中..'
          }
        case 2:
          return {
            color: 'green-6',
            text: '已完成'
          }
        case 3:
          return {
            color: 'red-6',
            text: '失敗'
          }
        default:
          return {
            color: 'grey-5',
            text: '未知狀態'
          }
      }
    },
    lineGroupStatusMap(statusCode) {
      switch (String(statusCode)) {
        case '1':
          return {
            color: '#F9D401',
            text: '排程中'
          }
        case '2':
          return {
            color: 'green-6',
            text: '成功'
          }
        case '3':
          return {
            color: 'red-6',
            text: '失敗或未送出'
          }
        case '4':
          return {
            color: 'blue-6',
            text: '發送中'
          }
        default:
          return {
            color: 'grey-5',
            text: '未知狀態'
          }
      }
    },
    lineGroupResultMap(resultCode) {
      if (resultCode === '' || resultCode === null || typeof resultCode === 'undefined') {
        return {
          color: 'grey-5',
          text: '無結果'
        }
      }
      return this.lineGroupStatusMap(resultCode)
    },
    hasLogValue(value) {
      if (typeof value === 'string') return value.trim() !== ''
      return value !== null && typeof value !== 'undefined'
    },
    hasOwnLogField(log, field) {
      return Object.prototype.hasOwnProperty.call(log || {}, field)
    },
    hasStructuredLineGroupFields(log) {
      return this.hasOwnLogField(log, 'template_name') || this.hasOwnLogField(log, 'custom_content')
    },
    shouldUseStructuredLineGroupContent(log) {
      if (!this.hasStructuredLineGroupFields(log)) return false

      const hasStructuredValue = this.hasLogValue(log.template_name) || this.hasLogValue(log.custom_content)
      return hasStructuredValue || !this.hasLogValue(log.content)
    },
    lineGroupTemplateDisplay(log) {
      return this.hasLogValue(log?.template_name) ? log.template_name : '未使用套版'
    },
    lineGroupTextContent(log) {
      if (this.shouldUseStructuredLineGroupContent(log)) return log.custom_content
      return log.content
    },
    lineGroupTextDisplay(log) {
      const content = this.lineGroupTextContent(log)
      return this.hasLogValue(content) ? content : '無文字內容'
    },
    lineGroupRecipientDisplay(log) {
      if (this.hasLogValue(log.patient_name)) return log.patient_name
      if ([1, 4].includes(Number(log.status))) return '名單準備中'
      return '無傳送對象資料'
    },
    lineGroupHasAnyContent(log) {
      return [
        this.shouldUseStructuredLineGroupContent(log) ? log.template_name : null,
        this.lineGroupTextContent(log),
        log.image_url,
      ].some(value => this.hasLogValue(value))
    },
    openImagePreview(imageUrl) {
      if (!this.hasLogValue(imageUrl)) return
      this.previewImageUrl = imageUrl
      this.imagePreviewDialog = true
    },
    closeImagePreview() {
      this.imagePreviewDialog = false
      this.previewImageUrl = null
    },
    isDetailDownloadDisabled(log) {
      if (this.downloadingDetailNo === log.no) return true

      const status = Number(log.status)
      if (this.currentSelectName === 'lineGroup') return [1, 4].includes(status)
      return status === 1
    },
    allowedDates(val) {
      // 紀錄以台灣日期查詢，「今天」也用台灣日期，國外的電腦才選得到台灣的今天
      const today = this.$moment(taiwanToday(), 'YYYY-MM-DD')
      const defaultAllow = this.$moment(val).isBetween(today.clone().subtract(90, 'days'), today, 'day', '(]')
      if (this.selectDate.length === 1) {
        return defaultAllow && (this.$moment(val).isBetween(this.$moment(this.selectDate[0]).subtract(30, 'days'), this.$moment(this.selectDate[0]), 'day', '(]') ||
          this.$moment(val).isBetween(this.$moment(this.selectDate[0]), this.$moment(this.selectDate[0]).add(30, 'days'), 'day', '()'))
      }

      return defaultAllow
    },
    handleSearch() {
      if (!(this.selectLogMode && this.selectDate.length))
        return this.$notify.warning('請選擇日期區間與記錄類型')

      const dates = [...this.selectDate].sort()
      const mode = this.selectLogMode.value
      this.confirmedLogFilter = {
        mode,
        dates,
        phone: this.canSearchPhone.includes(mode) ? this.searchPhone?.trim() || null : null,
      }
      this.currentSelectName = this.confirmedLogFilter.mode
      this.logTitle = `發送紀錄 - ${dates.join(' ~ ')} ${this.selectLogMode.text}`
      if (this.page === 1)
        this.search()
      else
        this.page = 1
    },
    search() {
      if (!this.confirmedLogFilter) return

      const requestId = ++this.searchRequestId
      this.loading = true
      const data = {
        ...this.logAxiosData,
        ...this.axiosParamData,
        page: this.page,
        limit: 10
      }
      let api

      switch (this.confirmedLogFilter.mode) {
        case 'lineGroup':
          api = getLineMessageGroupLogsAPI
          break
        case 'lineRemind':
          if (this.confirmedLogFilter.phone)
            data.phone = this.confirmedLogFilter.phone
          api = getLineMessageRemindLogsAPI
          break
        case 'smsRemind':
          if (this.confirmedLogFilter.phone)
            data.phone = this.confirmedLogFilter.phone
          api = getSmsRemindLogsAPI
          break
        case 'smsGroup':
          if (this.confirmedLogFilter.phone)
            data.phone = this.confirmedLogFilter.phone
          api = getSMSMessageGroupLogAPI
          break
        case 'voiceRemind':
          if (this.confirmedLogFilter.phone)
            data.phone = this.confirmedLogFilter.phone
          api = getVoiceRemindLogsAPI
          break
        case 'smartCare':
          if (this.confirmedLogFilter.phone)
            data.phone = this.confirmedLogFilter.phone
          api = getSmartCareLogsAPI
          break
        case 'miniApp':
          if (this.confirmedLogFilter.phone)
            data.phone = this.confirmedLogFilter.phone
          api = getMiniAppMessageLogsAPI
          break
      }
      clearTimeout(this.searchLogTimer)

      api(data).then(res => {
        if (this.isDestroyed || requestId !== this.searchRequestId) return

        this.logData = res.data.data
        this.length = Number(res.data.paginate.page_total)

        clearTimeout(this.searchLogTimer)
        this.searchLogTimer = setTimeout(() => {
          this.search()
        }, 1000 * 60)
      }).finally(() => {
        if (!this.isDestroyed && requestId === this.searchRequestId) this.loading = false
      })
    },
    downloadLog() {
      if (!this.confirmedLogFilter) return

      this.downloadingLog = true
      const data = {
        ...this.logAxiosData,
        ...this.axiosParamData
      }
      let api, fileName

      switch (this.confirmedLogFilter.mode) {
        case 'lineGroup':
          api = downloadLineMessageGroupLogsAPI
          fileName = 'grouplog'
          break
        case 'lineRemind':
          if (this.confirmedLogFilter.phone)
            data.phone = this.confirmedLogFilter.phone
          api = downloadLineMessageRemindLogsAPI
          fileName = 'remindlog'
          break
        case 'smsRemind':
          api = downloadSmsRemindLogsAPI
          fileName = 'smslog'
          break
        case 'smsGroup':
          api = downloadSmsMessageGroupLogsAPI
          fileName = 'grouplog'
          break
        case 'voiceRemind':
          api = downloadVoiceRemindLogsAPI
          fileName = 'voicelog'
          break
        case 'smartCare':
          if (this.confirmedLogFilter.phone)
            data.phone = this.confirmedLogFilter.phone
          api = downloadSmartCareLogsAPI
          fileName = 'smartcarelog'
          break
        case 'miniApp':
          if (this.confirmedLogFilter.phone)
            data.phone = this.confirmedLogFilter.phone
          api = downloadMiniAppMessageLogsAPI
          fileName = 'mini_app_message_log.csv'
          break
      }

      api(data).then(res => {
        const blob = new Blob([res.data], {type: 'text/csv;charset=utf-8;'});
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        const contentDisposition = res.headers['content-disposition'] || ''
        const responseFileName = contentDisposition.split("filename=")[1]?.replace(/['"]/g, '')
        link.setAttribute("href", url);
        link.setAttribute("download", responseFileName || fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).finally(() => {
        this.downloadingLog = false
      })
    },
    downloadDetailLog(downloadType, log) {
      if (this.isDetailDownloadDisabled(log)) {
        if (this.downloadingDetailNo !== log.no) this.$notify.warning('發送完成後才可下載詳細名單')
        return
      }

      const no = log.no
      let api

      switch (downloadType) {
        case 'lineGroup':
          api = downloadLineMessageGroupDetailLogsAPI
          break
        case 'smsGroup':
          api = downloadSmsMessageGroupDetailLogsAPI
          break
      }

      if (!api || !no) {
        return this.$notify.warning('請選擇群發記錄')
      }

      this.downloadingDetailNo = no
      api(no, this.axiosParamData).then(res => {
        // 從後端響應中讀取 Content-Type
        const contentType = res.headers['content-type'] || 'application/octet-stream';
        const blob = new Blob([res.data], { type: contentType });

        // 從 Content-Disposition 中提取檔案名稱
        const contentDisposition = res.headers['content-disposition'] || '';
        const fileName = contentDisposition.split("filename=")[1]?.replace(/['"]/g, '') || 'downloaded-file.xlsx';

        // 創建下載鏈接
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        link.style.visibility = 'hidden';

        // 觸發下載
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).finally(() => {
        this.downloadingDetailNo = null
      });
    }
  },

  mounted() {
    this.initializeFromRouteQuery()
  },

  watch: {
    page() {
      this.search()
    },
  },

  beforeDestroy() {
    this.isDestroyed = true
    this.searchRequestId += 1
    clearTimeout(this.searchLogTimer)
  },
}
</script>

<style scoped>
.bullet::before {
  content: "";
  background: #000000;
  padding-inline: 5px;
  height: 10px;
  margin-inline-end: 10px;
  display: inline-block;
}

.line-message-image-preview {
  display: block;
  padding: 0;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  cursor: pointer;
}

.message-log-card {
  border-color: #e0e0e0 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
}

.min-width-0 {
  min-width: 0;
}

.line-message-recipient {
  color: #303030;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
}

.line-message-recipient-label {
  margin-right: 8px;
  color: #757575;
  font-size: 13px;
  font-weight: 400;
}

.line-message-recipient-value--empty {
  color: #9e9e9e;
  font-weight: 400;
}

.line-message-meta {
  display: flex;
  align-items: center;
  color: #757575;
  font-size: 13px;
}

.line-message-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 128px;
  gap: 24px;
}

.line-message-field-label {
  margin-bottom: 6px;
  color: #757575;
  font-size: 13px;
  font-weight: 500;
}

.line-message-template-row {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}

.line-message-template-row .line-message-field-label {
  min-width: 64px;
}

.line-message-template-name {
  min-width: 0;
  padding: 3px 8px;
  overflow: hidden;
  border-radius: 4px;
  background: #f1f3f4;
  color: #424242;
  font-size: 13px;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.line-message-template-name--empty {
  color: #9e9e9e;
  font-style: italic;
}

.line-message-text {
  box-sizing: border-box;
  min-height: 96px;
  padding: 12px 14px;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  background: #f7f8fa;
  color: #424242;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.line-message-empty-image {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 128px;
  height: 64px;
  border: 1px dashed #e0e0e0;
  border-radius: 8px;
  color: #9e9e9e;
  font-size: 13px;
  background: #fafafa;
}

.line-message-all-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 96px;
  padding: 16px;
  border: 1px dashed #e0e0e0;
  border-radius: 8px;
  background: #f7f8fa;
  color: #9e9e9e;
  font-size: 13px;
}

.line-message-results {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #f7f8fa;
}

.line-message-result-item {
  display: flex;
  align-items: center;
}

.line-message-result-label {
  margin-right: 8px;
  color: #616161;
  font-size: 13px;
}

@media (max-width: 600px) {
  .line-message-header {
    flex-wrap: wrap;
  }

  .line-message-download {
    margin-top: 12px;
    margin-left: 0 !important;
  }

  .line-message-body {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
